import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import LayerDrawingFloor from '@/components/map/layers/planview-layers/LayerDrawingFloor.vue'
import LayerDrawingFloorTiles from '@/components/map/layers/planview-layers/LayerDrawingFloorTiles.vue'
import LayerDrawPoint from '@/components/map/layers/draw-layers/LayerDrawPoint.vue'
import LayerLevelMarkerPoi from '@/components/map/layers/planview-layers/LayerLevelMarkerPoi.vue'
import LayerLevelMarkerTickets from '@/components/map/layers/planview-layers/LayerLevelMarkerTickets.vue'
import LayerLevelZone from '@/components/map/layers/planview-layers/LayerLevelZone.vue'
import LayerOrthophotoFloor from '@/components/map/layers/planview-layers/LayerOrthophotoFloor.vue'
import LayerOrthophotoFloorTiles from '@/components/map/layers/planview-layers/LayerOrthophotoFloorTiles.vue'
import LayerRoom from '@/components/map/layers/planview-layers/LayerRoom.vue'
import LayerRoomData from '@/components/map/layers/planview-layers/LayerRoomData.vue'
import LayerRoomText from '@/components/map/layers/planview-layers/LayerRoomText.vue'
import { PLANVIEW_PREFIX } from '@/store/module-prefixes'
import { RESET_CURRENT_TOOL, SET_DRAW_POINT_FEATURE } from '@/store/mutation-types'
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import LayerColoredSpaces from './LayerColoredSpaces.vue'

const layerIdDrawingFloor = 'layer-drawing-floor'
const layerIdDrawingFloorTiles = 'layer-drawing-floor-tiles'
const layerIdOrthophotoFloor = 'layer-orthophoto-floor'
const layerIdOrthophotoFloorTiles = 'layer-orthophoto-floor-tiles'
const layerIdRoom = 'layer-room'
const layerIdRoomLevelBeneath = 'layer-room-level-beneath'
const layerIdRoomOutline = 'layer-room-outline'
const layerIdRoomPoi = 'layer-room-marker-poi'
const layerIdRoomPoiSelected = 'layer-room-marker-poi-selected'
const layerIdRoomText = 'layer-room-text'
const layerIdSensorData = 'layer-room-sensor-data'


export default /*@__PURE__*/_defineComponent({
  __name: 'PlanViewLayers',
  setup(__props) {

const piniaStore = useGeneralStore()
const store = useStore()
const { setCurrentOverlayModalPinia, setRightClickCoordinate, setShowOverlayModalPinia } =
  useGeneralStore()

const filteredDrawings = computed(() => piniaStore.filteredDrawings)
const filteredOrthophotos = computed(() => piniaStore.filteredOrthophotos)

const hasDrawingAccess = computed<boolean>(() => store.getters.hasDrawingAccess)
const hasOrthophotoAccess = computed<boolean>(() => store.getters.hasOrthophotoAccess)
const hasPoiReaderAccess = computed<boolean>(() => store.getters.hasPoiReaderAccess)
const hasZoneAccess = computed<boolean>(() => store.getters.hasZoneAccess)
const isDrawPointActive = computed<boolean>(() => store.getters.isDrawPointActive)

const selectedPoi = computed(() => store.state.selectedPoi)
const mountedMapLayerIds = computed(() => store.state.mountedMapLayerIds)
const filteredPlanviewLayers = computed(() => store.state[PLANVIEW_PREFIX].filteredLayers)
const isSensordataShowing = computed(() => store.state[PLANVIEW_PREFIX].isSensordataShowing)
const poisForSelectedLevel = computed(() => store.state[PLANVIEW_PREFIX].poisForSelectedLevel)

const fullPoi = computed(() => {
  let fullPoi = undefined
  if (selectedPoi.value) {
    fullPoi = poisForSelectedLevel.value.find((poi) => poi.id === selectedPoi.value.id)
  }
  return fullPoi
})

const layerAboveFloorImage = computed(() => {
  if (mountedMapLayerIds.value.some((layer) => layer.startsWith('layer-orthophoto-floor'))) {
    const orthophotoLayers = mountedMapLayerIds.value.filter((layer) =>
      layer.startsWith('layer-orthophoto-floor')
    )
    return orthophotoLayers[0]
  }
  if (mountedMapLayerIds.value.some((layer) => layer.startsWith('layer-drawing-floor'))) {
    const drawingLayers = mountedMapLayerIds.value.filter((layer) =>
      layer.startsWith('layer-drawing-floor')
    )
    return drawingLayers[0]
  }
  if (mountedMapLayerIds.value.includes(layerIdRoomLevelBeneath)) return layerIdRoomLevelBeneath
  return ''
})

const layerRoomTextIsMounted = computed(() => {
  return mountedMapLayerIds.value.includes(layerIdRoomText)
})

const layerAboveData = computed(() => {
  if (mountedMapLayerIds.value.includes(layerIdRoomPoiSelected)) return layerIdRoomPoiSelected
  if (mountedMapLayerIds.value.includes(layerIdRoomPoi)) return layerIdRoomPoi
  return layerRoomTextIsMounted.value ? layerIdRoomText : ''
})

const layerAboveZoneLayer = computed(() => {
  const roomDataLayerId = mountedMapLayerIds.value.find((layerId) =>
    [
      layerIdSensorData,
      'layer-heatmap-01',
      'LayerHeatmap02',
      'LayerHeatmapDynamic',
      'LayerHeatmapStatic'
    ].includes(layerId)
  )
  if (roomDataLayerId) return roomDataLayerId
  else return layerAboveData.value
})

const layerAboveRoomLayer = computed(() => {
  if (mountedMapLayerIds.value.includes('colored-spaces-layer')) return 'colored-spaces-layer'
  return layerRoomTextIsMounted.value ? layerIdRoomText : ''
})

const shouldShowLevelZones = computed(() => {
  const isLevelZoneVisible = filteredPlanviewLayers.value.some((layer) =>
    layer.types.some((type) => type.visible === true)
  )
  return isLevelZoneVisible
})

function handleDrawPoint(point) {
  store.commit(SET_DRAW_POINT_FEATURE, { point })
  store.commit(RESET_CURRENT_TOOL)
}

function handleDrawPointMobile(point, editMode) {
  if (!point) return
  const rightClickCoordinate = point.geometry.coordinates
  if (!editMode) setRightClickCoordinate(rightClickCoordinate)
  setCurrentOverlayModalPinia('createAssets')
  setShowOverlayModalPinia(true)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (filteredOrthophotos.value.length && hasOrthophotoAccess.value)
      ? (_openBlock(), _createBlock(LayerOrthophotoFloor, {
          key: 0,
          layerId: layerIdOrthophotoFloor,
          before: layerAboveFloorImage.value
        }, null, 8, ["before"]))
      : _createCommentVNode("", true),
    (filteredOrthophotos.value.length && hasOrthophotoAccess.value)
      ? (_openBlock(), _createBlock(LayerOrthophotoFloorTiles, {
          key: 1,
          layerId: layerIdOrthophotoFloorTiles,
          before: layerAboveFloorImage.value
        }, null, 8, ["before"]))
      : _createCommentVNode("", true),
    (filteredDrawings.value.length && hasDrawingAccess.value && layerRoomTextIsMounted.value)
      ? (_openBlock(), _createBlock(LayerDrawingFloor, {
          key: 2,
          layerId: layerIdDrawingFloor,
          before: layerIdRoomLevelBeneath
        }))
      : _createCommentVNode("", true),
    (filteredDrawings.value.length && hasDrawingAccess.value && layerRoomTextIsMounted.value)
      ? (_openBlock(), _createBlock(LayerDrawingFloorTiles, {
          key: 3,
          layerId: layerIdDrawingFloorTiles,
          before: layerIdRoomLevelBeneath
        }))
      : _createCommentVNode("", true),
    (layerRoomTextIsMounted.value)
      ? (_openBlock(), _createBlock(LayerRoom, {
          key: 4,
          before: layerAboveRoomLayer.value,
          layerIdRoomLevelBeneath: layerIdRoomLevelBeneath,
          layerIdRoomOutline: layerIdRoomOutline,
          layerIdRoom: layerIdRoom,
          layerIdRoomText: layerIdRoomText
        }, null, 8, ["before"]))
      : _createCommentVNode("", true),
    (layerRoomTextIsMounted.value)
      ? (_openBlock(), _createBlock(LayerColoredSpaces, { key: 5 }))
      : _createCommentVNode("", true),
    (shouldShowLevelZones.value && hasZoneAccess.value && layerRoomTextIsMounted.value)
      ? (_openBlock(), _createBlock(LayerLevelZone, {
          key: 6,
          before: layerAboveZoneLayer.value,
          layerIdRoomText: layerIdRoomText
        }, null, 8, ["before"]))
      : _createCommentVNode("", true),
    (layerRoomTextIsMounted.value && isSensordataShowing.value)
      ? (_openBlock(), _createBlock(LayerRoomData, {
          key: 7,
          before: layerAboveData.value,
          layerIdSensorData: layerIdSensorData
        }, null, 8, ["before"]))
      : _createCommentVNode("", true),
    (layerRoomTextIsMounted.value && hasPoiReaderAccess.value && !isDrawPointActive.value)
      ? (_openBlock(), _createBlock(LayerLevelMarkerPoi, {
          key: 8,
          before: layerIdRoomText,
          layerId: layerIdRoomPoi,
          layerIdSelected: layerIdRoomPoiSelected
        }))
      : _createCommentVNode("", true),
    (layerRoomTextIsMounted.value && _unref(piniaStore).showFeatureErrands && !isDrawPointActive.value)
      ? (_openBlock(), _createBlock(LayerLevelMarkerTickets, { key: 9 }))
      : _createCommentVNode("", true),
    (isDrawPointActive.value)
      ? (_openBlock(), _createBlock(LayerDrawPoint, {
          key: 10,
          withinLayerId: layerIdRoomLevelBeneath,
          pointSource: fullPoi.value,
          onSubmitPoint: handleDrawPoint,
          onSubmitPointMobile: handleDrawPointMobile
        }, null, 8, ["pointSource"]))
      : _createCommentVNode("", true),
    _createVNode(LayerRoomText, { layerId: layerIdRoomText })
  ], 64))
}
}

})